import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import { getWhitelabelConfig } from '@/lib/whitelabel';
import BlockRenderer from '@/components/BlockRenderer';
import useTracking from '@/hooks/useTracking';
import { Loader2 } from 'lucide-react';

export default function PublicPage() {
  const { slug } = useParams();
  const subdomain = useStoreSubdomain();
  const [page, setPage] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { trackEvent } = useTracking(store?.store_extras);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, storeRes] = await Promise.all([
          api.get(`/store/${subdomain}/page/${slug}`),
          api.get(`/store/${subdomain}`)
        ]);
        setPage(pageRes.data);
        setStore(storeRes.data);
      } catch (err) {
        setError(err?.response?.status === 404 ? 'Halaman tidak ditemukan' : 'Gagal memuat halaman');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [subdomain, slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-5xl mb-4">📄</p>
          <h1 className="text-xl font-bold text-slate-800 mb-2">{error || 'Halaman tidak ditemukan'}</h1>
          <a href={`/store/${subdomain}`} className="text-blue-600 hover:underline text-sm">Kembali ke toko</a>
        </div>
      </div>
    );
  }

  const pc = store?.primary_color || '#2D6CDF';
  const wl = getWhitelabelConfig(store);
  const storeName = wl.brandName;

  return (
    <div className="min-h-screen bg-white">
      {/* Simple header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <a href={`/store/${subdomain}`} className="font-bold text-lg" style={{ color: pc }}>
            {wl.logoUrl ? <img src={wl.logoUrl} alt={storeName} className="h-8" /> : storeName}
          </a>
          <a href={`/store/${subdomain}`} className="text-sm text-slate-500 hover:text-slate-800">← Kembali ke Toko</a>
        </div>
      </header>

      {/* SEO title */}
      {page.seo_title && <title>{page.seo_title}</title>}

      {/* Page content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {page.title && <h1 className="text-3xl font-bold text-slate-900 mb-8">{page.title}</h1>}
        <BlockRenderer
          blocks={page.blocks || []}
          subdomain={subdomain}
          primaryColor={pc}
        />
      </main>

      {/* Simple footer */}
      <footer className="border-t border-slate-200 py-6 text-center text-sm text-slate-400">
        {wl.footerText || `\u00A9 ${new Date().getFullYear()} ${storeName}`}
        {!wl.hidePoweredBy && (
          <p className="text-xs mt-2">Powered by Tokomoe</p>
        )}
      </footer>
      {wl.customCss && <style>{wl.customCss}</style>}
    </div>
  );
}
